import React from "react"
import { SbDialog } from "components/commons/sb-dialog"
import { Trans, t } from "@lingui/macro"
import {
	Box,
	TextField,
	Typography,
} from "@mui/material"
import { Autocomplete } from "@mui/lab"
import {
	MatesComponent,
	MatesComponentState,
} from "components/mates-component"
import withUseRouter, { UseRouterParams } from "services/router"
import {
	Brand,
	BrandManagementRequest,
	PaginatedResponse,
} from "models"
import { BrandManagementServices } from "services/brand-management.services"
import {
	ErrorResponse,
	Loading,
	RestResponse,
} from "@sinossi/mates-react-library"
import Icon from "@mdi/react"
import {
	mdiEmoticonExcited,
	mdiEmoticonNeutral,
	mdiEmoticonSad,
} from "@mdi/js"
import { theme } from "index"
import { SbSnackbar } from "components/commons/sb-snackbar"
import { BrandOption, NewMatchRequest } from "../model"
import { MatchService } from "services/match.service"

interface ProposalCreationDialogProps extends UseRouterParams {
	onClose: () => void
	closeWithRefresh: () => void
	assetId: string
}

interface ProposalCreationDialogState extends MatesComponentState {
	loading: boolean
	loadingPage: boolean
	brandOptions: BrandOption[]
	brandValue: BrandOption | null
	brandInputValue: string
	timeout: any
	compatibility: number
	snackbarData: {
		openNotification: boolean,
		severity: 'error' | 'info' | 'success' | 'warning',
		notificationMessage: string
	}
}

class ProposalCreationDialog extends React.Component<ProposalCreationDialogProps, ProposalCreationDialogState> {
	private readonly matesComponent: MatesComponent<ProposalCreationDialogProps, ProposalCreationDialogState> = new MatesComponent<ProposalCreationDialogProps, ProposalCreationDialogState>(this)

	constructor(props: ProposalCreationDialogProps) {
		super(props)
		this.state = {
			...this.matesComponent.setupState(),
			brandOptions: [],
			loadingPage: false,
			brandValue: null,
			brandInputValue: "",
			timeout: null,
			compatibility: 1,
			snackbarData: {
				openNotification: false,
				severity: "info",
				notificationMessage: ""
			}
		}
	}

	private fetchBrand = (input: string) => {
		if (this.state.timeout) {
			clearTimeout(this.state.timeout)
		}
		this.setState(() => ({
			timeout: setTimeout(() => this.handleBrandChange(input), 1000)
		}))
	}

	private handleBrandChange = (value: string) => {
		this.setState(() => ({
			loading: true
		}))

		if (value !== "") {
			let request: BrandManagementRequest = {
				name: value,
				pageSize: 50
			}

			BrandManagementServices.brandManagementData(request, this.handleBrandChangeCallback, this.matesComponent.onError)
		}
	}

	private handleBrandChangeCallback = (response: RestResponse<PaginatedResponse<Brand>, ErrorResponse>) => {
		if (response.hasError()) {
			this.matesComponent.onError(response)
			return
		}

		if (response.data) {
			let brandOptions: BrandOption[] = []

			response.data!.items.map((brand) => brandOptions.push({
				id: brand.id,
				name: brand.name
			}))

			this.setState(() => ({
				brandOptions: brandOptions,
				loading: false
			}))
		}
	}

	private validateAndSubmit = () => {
		if (!this.state.brandValue) {
			this.setState(() => ({
				snackbarData: {
					openNotification: true,
					severity: 'error',
					notificationMessage: t({ id: "proposal-creation-dialog.brand-value-not-found", message: "Impossibile procedere. Inserire brand e riprovare." })
				}
			}))
			return
		}

		if (![33, 66, 100].includes(this.state.compatibility)) {
			this.setState(() => ({
				snackbarData: {
					openNotification: true,
					severity: 'error',
					notificationMessage: t({ id: "proposal-creation-dialog.compatibility-value-not-found", message: "Impossibile procedere. Inserire compatibilità e riprovare." })
				}
			}))
			return
		}

		let request: NewMatchRequest = {
			assetId: this.props.assetId,
			brandId: this.state.brandValue.id,
			compatibility: this.state.compatibility,
		}

		this.setState(() => ({
			loadingPage: true
		}))

		MatchService.createNewMatch(request, this.submitCallback, this.matesComponent.onError)
	}

	private submitCallback = (response: RestResponse<any, ErrorResponse>) => {
		if (response.hasError()) {
			this.setState(() => ({
				snackbarData: {
					openNotification: true,
					notificationMessage: t({ id: "proposal-creation-dialog.submit-error", message: "Non è stato possibile inserire la proposta" }),
					severity: "error"
				}
			}), () => setTimeout(this.props.closeWithRefresh, 1000))

			return
		}

		this.setState(() => ({
			snackbarData: {
				openNotification: true,
				notificationMessage: t({ id: "proposal-creation-dialog.submit-success", message: "Proposta inserita con successo." }),
				severity: "success"
			}
		}), () => setTimeout(this.props.closeWithRefresh, 1000))
	}

	render() {
		return (
			<>
				{this.state.loadingPage ? <Loading show /> :
					<SbDialog
						width={"sm"}
						titleText={t({ id: "proposal-creation.insert-new", message: "Inserisci nuova proposta" })}
						closeButton
						confirmButton
						onClose={this.props.onClose}
						onConfirm={this.validateAndSubmit}
					>
						<Box sx={{ width: "100%", mt: 1 }}>
							<Autocomplete
								loading={this.state.loading}
								getOptionLabel={(option) => option.name}
								filterOptions={(x) => x}
								options={this.state.brandOptions}
								autoComplete
								includeInputInList
								filterSelectedOptions
								value={this.state.brandValue}
								noOptionsText={t({ id: "create-contract-modal.no-brand", message: "Inizia a digitare per avviare la ricerca" })}
								onChange={(event, newValue) => {
									if (newValue) {
										this.setState((prevState) => ({
											brandValue: newValue, brandOptions: [newValue, ...prevState.brandOptions]
										}))
									}
								}}
								onInputChange={(event, newInputValue) => {
									this.setState({ brandInputValue: newInputValue }, () => {
										if (this.state.brandInputValue === '') {
											this.setState({ brandOptions: this.state.brandValue ? [this.state.brandValue] : [] });
										}
										else {
											this.fetchBrand(this.state.brandInputValue)
										}
									})
								}}
								renderInput={(params) => (
									<TextField {...params}
										label={t({ id: "create-contract-modal.select-brand", message: "Brand" })}
										fullWidth
										required
										size="small"
									/>
								)}
							/>
						</Box>
						<Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 3 }}>
							<Typography sx={{ fontWeight: "600", minWidth: "fit-content" }}>
								<Trans id={"proposal-creation-dialog.compatibility"}>
									Compatibilità:
								</Trans>
							</Typography>
							<Box sx={{ display: "flex", gap: 1 }}>
								<Box
									onClick={() => this.setState(() => ({ compatibility: 100 }))}
									sx={{
										cursor: "pointer",
										p: 0.5,
										borderRadius: 1,
										'&:hover': { backgroundColor: 'grey.100' }
									}}
								>
									<Icon path={mdiEmoticonExcited} size={1} style={{ color: this.state.compatibility === 100 ? theme.palette.info.main : "#adb5bd" }} />
								</Box>
								<Box
									onClick={() => this.setState(() => ({ compatibility: 66 }))}
									sx={{
										cursor: "pointer",
										p: 0.5,
										borderRadius: 1,
										'&:hover': { backgroundColor: 'grey.100' }
									}}
								>
									<Icon path={mdiEmoticonNeutral} size={1} style={{ color: this.state.compatibility === 66 ? theme.palette.info.main : "#adb5bd" }} />
								</Box>
								<Box
									onClick={() => this.setState(() => ({ compatibility: 33 }))}
									sx={{
										cursor: "pointer",
										p: 0.5,
										borderRadius: 1,
										'&:hover': { backgroundColor: 'grey.100' }
									}}
								>
									<Icon path={mdiEmoticonSad} size={1} style={{ color: this.state.compatibility === 33 ? theme.palette.info.main : "#adb5bd" }} />
								</Box>
							</Box>
						</Box>
					</SbDialog>}
				<SbSnackbar
					snackbarData={this.state.snackbarData}
					handleCloseNotification={() => {
						this.setState((prevState) => ({
							snackbarData: { ...prevState.snackbarData, openNotification: false }
						}))
					}} />
			</>
		)
	}
}

export default withUseRouter(ProposalCreationDialog)
